/**
 * @module app.core
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults, IApiSettingsResults } from 'app/shared/lib/api/api-types';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

/**
 * Provides access to general Core lookup API models
 */
@transient()
@autoinject
export class LookupService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(protected readonly api: Api) { }


    ////****************** Settings *************************************************////
    public async loadBusinessHourSettings(): Promise<IApiSettingsResults> {
        return this.api.get(
            this.api.settings('businesshours')
        );
    }


    ////****************** General **************************************************////

    /**
     * Load all `Core.Country` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadCountries(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.Country>> {
        const query = this.api
            .query('countries')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.BusinessHour` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadBusinessHours(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.BusinessHour>> {
        const query = this.api
            .query('businesshours')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Credential` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadCredentials(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.Credential>> {
        const query = this.api
            .query('admin/credentials')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Gender` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadGenders(options?: Partial<IQueryLoadOptions>, includes: string[] = [], availableAsIdentity: boolean = false, availableAsSex: boolean = false, fields: string[] = []): Promise<IApiQueryResults<Core.Gender>> {
        const query = this.api
            .query('genders')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        if(availableAsIdentity) query.filterBoolean('flag.availableAsIdentity', true);
        if(availableAsSex) query.filterBoolean('flag.availableAsSex', true);

        return await this.api.get(query);
    }

    /**
     * Load all `Core.Language` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadLanguages(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Language>> {
        const query = this.api
            .query('cultures/languages')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Race` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadRaces(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Race>> {
        const query = this.api
            .query('races')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Ethnicity` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadEthnicities(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Ethnicity>> {
        const query = this.api
            .query('ethnicities')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.TimeZone` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadTimeZones(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.TimeZone>> {
        const query = this.api
            .query('timeZones')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Application` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadApplications(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.Application>> {
        const query = this.api
            .query('applications')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    ////****************** Addresses ************************************************////

    /**
     * Load all `Core.AddressRegion` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadStates(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.AddressRegion>> {
        const query = this.api
            .query('addresses/regions')
            .filterStringArray('designation.code', ['ST', 'DI', 'TE'])
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.AddressRegion` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadRegions(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.AddressRegion>> {
        const query = this.api
            .query('addresses/regions')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load `Core.AddressArea` models by region
     * @param options query load options
     * @param includes related models to include
     */
    public async loadAreasByRegion(addressRegionId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.AddressArea>> {
        const query = this.api
            .query(`addresses/regions/${addressRegionId}/areas`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async searchUnits(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.UnitOfMeasure>> {
        const query = this.api
            .query(`core/measurements/units`)
            .search(searchValue)
            .feature('SmartSearch')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////


    ////****************** Actions **************************************************////

    public async activateBusinessHour(businessHourId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `businesshours/${businessHourId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveBusinessHour(businessHour: Core.BusinessHour): Promise<IApiActionResults> {
        const actionCode = !businessHour.id ? 'insert' : 'update';
        const url = `businessHours/${businessHour.id ? `${businessHour.id}` : ``}`;
        const action = this.api.action(actionCode, url, businessHour);
        return await this.api.post(action);
    }

    public async activateCredential(credentialId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `admin/credentials/${credentialId}`, { isActive: isActive });

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveCredential(credential: Core.Credential): Promise<IApiActionResults> {
        const actionName = !credential.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `admin/credentials/${actionName === 'update' ? credential.id : ''}`, credential);

        return await this.api.post(action);
    }
}
